import * as d3 from "d3";
import React, { useRef, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { visualizationData } from "./data/data";
import { calculateTotalTests } from "./utils";

interface D3ChartProps {
  onNodeSelect?: (nodeData: any) => void;
  onZoomChange?: (isAtRoot: boolean) => void;
}

// Fast estimation-based font size calculation with caching
const fontSizeCache = new Map<string, { fontSize: number; lines: string[] }>();
function estimateMaxMultilineFontSize(
  text: string,
  r: number,
  fontWeight = "bold",
  fontFamily = "Inter, system-ui, sans-serif",
  maxFontSize = 40,
  minFontSize = 18,
) {
  // Use a cache key based on text and radius
  const cacheKey = `${text}|${Math.round(r)}`;
  if (fontSizeCache.has(cacheKey)) return fontSizeCache.get(cacheKey)!;
  // Estimate max chars per line based on node diameter
  let fontSize = maxFontSize;
  let lines: string[] = [];
  let fits = false;
  while (fontSize >= minFontSize) {
    const maxCharsPerLine = Math.floor((r * 1.8) / (fontSize * 0.6));
    lines = wrapText(text, r, fontSize);
    const longestLine = lines.reduce(
      (a, b) => (a.length > b.length ? a : b),
      "",
    );
    // Estimate width: chars * 0.6 * fontSize, height: lines * fontSize
    const estWidth = longestLine.length * 0.6 * fontSize;
    const estHeight = lines.length * fontSize;
    if (estWidth <= r * 1.7 && estHeight <= r * 1.2) {
      fits = true;
      break;
    }
    fontSize -= 1;
  }
  if (!fits) {
    fontSize = minFontSize;
    lines = wrapText(text, r, minFontSize);
  }
  const result = { fontSize, lines };
  fontSizeCache.set(cacheKey, result);
  return result;
}

// Accurate DOM-based font size calculation for visible labels only
function getMaxMultilineFontSize(
  text: string,
  r: number,
  fontWeight = "bold",
  fontFamily = "Inter, system-ui, sans-serif",
  maxFontSize = 40,
  minFontSize = 18,
) {
  if (typeof document === "undefined") return minFontSize;
  let fontSize = maxFontSize;
  let fits = false;
  let lines: string[] = [];
  const svg = d3
    .select(document.body)
    .append("svg")
    .attr("style", "position: absolute; left: -9999px; top: -9999px;");
  const textElem = svg
    .append("text")
    .attr("font-family", fontFamily)
    .attr("font-weight", fontWeight);
  while (fontSize >= minFontSize) {
    lines = wrapText(text, r, fontSize);
    textElem.selectAll("tspan").remove();
    lines.forEach((line, i) => {
      textElem
        .append("tspan")
        .text(line)
        .attr("x", 0)
        .attr("y", i * fontSize);
    });
    const bbox = (textElem.node() as SVGTextElement).getBBox();
    if (bbox.width <= r * 1.7 && bbox.height <= r * 1.2) {
      fits = true;
      break;
    }
    fontSize -= 1;
  }
  svg.remove();
  if (!fits) {
    lines = wrapText(text, r, minFontSize);
    return { fontSize: minFontSize, lines };
  }
  return { fontSize, lines };
}

export const D3Chart: React.FC<D3ChartProps> = ({
  onNodeSelect,
  onZoomChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { agentId } = useParams({ from: "/ai-agents/$agentId" });

  useEffect(() => {
    // Clear previous SVG/chart
    if (chartRef.current) {
      chartRef.current.innerHTML = "";
    }

    // Track current hovered node
    let currentHoveredNode: any = null;

    // Function to render the chart
    const renderChart = () => {
      // Clear previous SVG/chart
      if (chartRef.current) {
        chartRef.current.innerHTML = "";
      }

      // Transform visualizationData to match D3 hierarchy format
      const transformData = (data: any): any => {
        const transformedNode: any = {
          id: data.id,
          name: data.name,
          value: data.testCoverage || 0, // Use testCoverage as the value for sizing
          children: [],
        };

        // Add children (subtopics) if they exist
        if (data.children) {
          transformedNode.children.push(...data.children.map(transformData));
        }

        // Add tests as leaf nodes if they exist
        if (data.tests && data.tests.length > 0) {
          const testNodes = data.tests.map((test: any, index: number) => ({
            id: test.id || `${data.id}-test-${index}`,
            name:
              test.input.length > 50
                ? test.input.substring(0, 50) + "..."
                : test.input,
            value: test.success ? 1 : 0.5, // Smaller value for tests, based on success
            testData: test, // Store the full test data
            isTest: true, // Flag to identify test nodes
            originalInput: test.input, // Store the full input text
          }));
          transformedNode.children.push(...testNodes);
        }

        return transformedNode;
      };

      const data = transformData(visualizationData);

      // Chart dimensions
      console.log(chartRef.current?.offsetWidth);
      const width = chartRef.current?.offsetWidth || 100;

      const height = width;

      // Calculate responsive font size based on screen width
      const getResponsiveFontSize = () => {
        const screenWidth = window.innerWidth;
        if (screenWidth < 640) return 14; // Mobile
        if (screenWidth < 1024) return 20; // Tablet
        if (screenWidth < 1440) return 24; // Small desktop
        return 27; // Large desktop (original size)
      };

      const fontSize = getResponsiveFontSize();

      // Create the color function based on test pass/fail ratio
      const getNodeColor = (d: any) => {
        if (d.data.isTest) {
          // Test nodes - color based on success/failure
          const opacity = Math.min(0.1 + d.depth * 0.15, 0.8);
          return d.data.testData.success
            ? `rgba(16, 185, 129, ${opacity})` // Green for passed tests
            : `rgba(239, 68, 68, ${opacity})`; // Red for failed tests
        } else if (!d.children) {
          // Leaf nodes - color based on pass/fail ratio with depth-based opacity
          const totals = calculateTotalTests(d.data);
          if (totals.total === 0) return "#374151"; // Dark gray for no tests
          const passRatio = totals.passed / totals.total;
          const opacity = Math.min(0.1 + d.depth * 0.15, 0.8); // Increase opacity with depth
          if (passRatio >= 0.8) return `rgba(16, 185, 129, ${opacity})`; // Green for good pass rate
          if (passRatio >= 0.5) return `rgba(245, 158, 11, ${opacity})`; // Orange for moderate pass rate
          return `rgba(239, 68, 68, ${opacity})`; // Red for poor pass rate
        } else {
          // Parent nodes - use depth-based color with increasing opacity
          const opacity = Math.min(0.1 + d.depth * 0.15, 0.8); // Increase opacity with depth
          const colors = [
            "transparent",
            `rgba(59, 130, 246, ${opacity})`,
            `rgba(99, 102, 241, ${opacity})`,
            `rgba(139, 92, 246, ${opacity})`,
            `rgba(168, 85, 247, ${opacity})`,
            `rgba(236, 72, 153, ${opacity})`,
          ];
          return colors[d.depth] || colors[colors.length - 1];
        }
      };

      // Compute the layout
      const pack = (data: any) =>
        d3.pack().size([width, height]).padding(8)(
          d3
            .hierarchy(data)
            .sum((d) => d.value || 0)
            .sort((a, b) => (b.value || 0) - (a.value || 0)),
        );

      const root = pack(data);

      // Initialize focus to root before rendering labels
      let focus = root;

      // Create the SVG container
      const svg = d3
        .create("svg")
        .attr("viewBox", `-${width / 2} -${height / 2} ${width} ${height}`)
        .attr("width", width)
        .attr("height", height)
        .attr(
          "style",
          `max-width: 100%; height: auto; display: block; margin: 0 auto; background: transparent;`,
        );

      // Add gradient definitions
      const defs = svg.append("defs");

      // Green gradient for good pass rates
      const greenGradient = defs
        .append("radialGradient")
        .attr("id", "greenGradient")
        .attr("cx", "30%")
        .attr("cy", "30%");
      greenGradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#10b981")
        .attr("stop-opacity", 1);
      greenGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#059669")
        .attr("stop-opacity", 1);

      // Orange gradient for moderate pass rates
      const orangeGradient = defs
        .append("radialGradient")
        .attr("id", "orangeGradient")
        .attr("cx", "30%")
        .attr("cy", "30%");
      orangeGradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#f59e0b")
        .attr("stop-opacity", 1);
      orangeGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#d97706")
        .attr("stop-opacity", 1);

      // Red gradient for poor pass rates
      const redGradient = defs
        .append("radialGradient")
        .attr("id", "redGradient")
        .attr("cx", "30%")
        .attr("cy", "30%");
      redGradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#ef4444")
        .attr("stop-opacity", 1);
      redGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#dc2626")
        .attr("stop-opacity", 1);

      // Blue gradients for parent nodes
      const blueGradient1 = defs
        .append("radialGradient")
        .attr("id", "blueGradient1")
        .attr("cx", "30%")
        .attr("cy", "30%");
      blueGradient1
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#3b82f6")
        .attr("stop-opacity", 0.8);
      blueGradient1
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#1d4ed8")
        .attr("stop-opacity", 0.8);

      const blueGradient2 = defs
        .append("radialGradient")
        .attr("id", "blueGradient2")
        .attr("cx", "30%")
        .attr("cy", "30%");
      blueGradient2
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#6366f1")
        .attr("stop-opacity", 0.8);
      blueGradient2
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#4338ca")
        .attr("stop-opacity", 0.8);

      const blueGradient3 = defs
        .append("radialGradient")
        .attr("id", "blueGradient3")
        .attr("cx", "30%")
        .attr("cy", "30%");
      blueGradient3
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#8b5cf6")
        .attr("stop-opacity", 0.8);
      blueGradient3
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#6d28d9")
        .attr("stop-opacity", 0.8);

      const blueGradient4 = defs
        .append("radialGradient")
        .attr("id", "blueGradient4")
        .attr("cx", "30%")
        .attr("cy", "30%");
      blueGradient4
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#a855f7")
        .attr("stop-opacity", 0.8);
      blueGradient4
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#7c3aed")
        .attr("stop-opacity", 0.8);

      const purpleGradient = defs
        .append("radialGradient")
        .attr("id", "purpleGradient")
        .attr("cx", "30%")
        .attr("cy", "30%");
      purpleGradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#ec4899")
        .attr("stop-opacity", 0.8);
      purpleGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#be185d")
        .attr("stop-opacity", 0.8);

      // Append the nodes - separate circles for topics and pentagons for tests
      const nodes = root.descendants().slice(1);
      const topicNodes = nodes.filter((d: any) => !d.data.isTest);
      const testNodes = nodes.filter((d: any) => d.data.isTest);

      // Create circles for topics
      const topicCircles = svg
        .append("g")
        .selectAll("circle")
        .data(topicNodes)
        .join("circle")
        .attr("fill", (d: any) => getNodeColor(d))
        .attr("stroke", (d: any) =>
          d.children ? "#1f2937" : "rgba(255,255,255,0.1)",
        )
        .attr("cursor", "pointer")
        .attr("stroke-width", (d: any) => (d.children ? 2 : 1))
        .on("mouseover", function (event: any, d: any) {
          currentHoveredNode = d;
          d3.select(this).attr("stroke", "#60a5fa").attr("stroke-width", 3);
        })
        .on("mouseout", function (event: any, d: any) {
          currentHoveredNode = null;
          d3.select(this)
            .attr("stroke", (d: any) =>
              d.children ? "#1f2937" : "rgba(255,255,255,0.1)",
            )
            .attr("stroke-width", (d: any) => (d.children ? 2 : 1));
        })
        .on("click", (event: any, d: any) => {
          // Emit node data to parent component on click
          if (onNodeSelect) {
            onNodeSelect(d.data);
          }

          if (d.children && d.children.length > 0) {
            // Parent node with children - zoom behavior
            if (focus !== d) {
              zoom(event, d);
              event.stopPropagation();
            }
          } else {
            // Leaf node - navigate to topic details page
            if (d.data.id) {
              navigate({
                to: "/ai-agents/$agentId/topics/$topicId",
                params: { agentId, topicId: d.data.id },
              });
            } else {
              console.warn("No ID found for node:", d.data);
            }
          }
        });

      // Helper to create a pentagon path centered at (0,0)
      function pentagonPath(size: number) {
        const points = [];
        for (let i = 0; i < 5; i++) {
          const angle = ((Math.PI * 2) / 5) * i - Math.PI / 2;
          const x = Math.cos(angle) * size;
          const y = Math.sin(angle) * size;
          points.push(`${x},${y}`);
        }
        return `M ${points[0]} L ${points.slice(1).join(" ")} Z`;
      }

      // Create pentagons for tests
      const testTriangles = svg
        .append("g")
        .selectAll("path")
        .data(testNodes)
        .join("path")
        .attr("fill", (d: any) => getNodeColor(d))
        .attr("stroke", "rgba(255,255,255,0.1)")
        .attr("cursor", "pointer")
        .attr("stroke-width", 1)
        // Draw pentagon centered at (0,0), then move with transform
        .attr("d", (d: any) => pentagonPath(d.r))
        .attr("transform", (d: any) => `translate(${d.x},${d.y})`)
        .on("mouseover", function (event: any, d: any) {
          currentHoveredNode = d;
          d3.select(this).attr("stroke", "#60a5fa").attr("stroke-width", 3);
        })
        .on("mouseout", function (event: any, d: any) {
          currentHoveredNode = null;
          d3.select(this)
            .attr("stroke", "rgba(255,255,255,0.1)")
            .attr("stroke-width", 1);
        })
        .on("click", (event: any, d: any) => {
          // Emit node data to parent component on click
          if (onNodeSelect) {
            onNodeSelect(d.data);
          }

          // Test node - zoom to the test and emit data
          if (focus !== d) {
            zoom(event, d);
            event.stopPropagation();
          }
        });

      // Store both selections for zoom operations
      const allNodes = { topicCircles, testTriangles };

      // Helper: should this label be shown?
      function shouldShowLabel(d: any, focus: any) {
        if (d.data.isTest) {
          // Show if the test node's parent is focused, or if the test node itself is focused
          return d.parent === focus || focus === d;
        } else {
          // Show if this node is a direct child of the focus,
          // or if this node is a leaf (no children, no tests) and is the focus
          const isLeafNoTests =
            (!d.children || d.children.length === 0) &&
            (!d.data.tests || d.data.tests.length === 0);
          return d.parent === focus || (focus === d && isLeafNoTests);
        }
      }

      // Remove the separate testLabels group and use only the label group for all nodes
      // Append the text labels (unified for both test and topic nodes)
      const label = svg
        .append("g")
        .attr("pointer-events", "none")
        .attr("text-anchor", "middle")
        .selectAll("text")
        .data(root.descendants())
        .join("text")
        .attr("class", (d: any) =>
          d.data.isTest ? "test-label" : "topic-label",
        )
        .attr("transform", (d: any) => `translate(${d.x},${d.y})`)
        .style("display", function (this: any, d: any) {
          return shouldShowLabel(d, focus) ? "inline" : "none";
        })
        .style("fill-opacity", function (this: any, d: any) {
          return shouldShowLabel(d, focus) ? 1 : 0;
        })
        .style("fill", "#fff")
        .attr("fill", "#fff")
        .style("fill-opacity", 1)
        .attr("fill-opacity", 1)
        .style("stroke", "#000")
        .attr("stroke", "#000")
        .style("stroke-opacity", 1)
        .attr("stroke-opacity", 1)
        .style("stroke-width", "1.5px")
        .attr("stroke-width", "1.5px")
        .style("paint-order", "stroke fill")
        .attr("paint-order", "stroke fill")
        .style("font-weight", "bold")
        .attr("font-weight", "bold")
        .style("font-family", "Arial, sans-serif")
        .attr("font-family", "Arial, sans-serif")
        .each(function (this: any, d: any) {
          const text = d.data.isTest
            ? d.data.originalInput || "Test"
            : d.data.name;
          const fontWeight = "bold";
          const result = estimateMaxMultilineFontSize(text, d.r, fontWeight);
          d3.select(this).selectAll("tspan").remove();
          result.lines.forEach((line: string, i: number) => {
            d3.select(this)
              .append("tspan")
              .attr("x", 0)
              .attr(
                "y",
                `${i * result.fontSize - ((result.lines.length - 1) * result.fontSize) / 2}`,
              )
              .style("font-size", `${result.fontSize}px`)
              .attr("font-size", `${result.fontSize}px`)
              .style("font-weight", "bold")
              .attr("font-weight", "bold")
              .style("fill", "#fff")
              .attr("fill", "#fff")
              .style("fill-opacity", 1)
              .attr("fill-opacity", 1)
              .style("stroke", "#000")
              .attr("stroke", "#000")
              .style("stroke-opacity", 1)
              .attr("stroke-opacity", 1)
              .style("stroke-width", "1.5px")
              .attr("stroke-width", "1.5px")
              .style("paint-order", "stroke fill")
              .attr("paint-order", "stroke fill")
              .style("font-family", "Arial, sans-serif")
              .attr("font-family", "Arial, sans-serif")
              .text(line);
          });
        });

      // Create the zoom behavior and zoom immediately in to the initial focus node
      svg.on("click", (event: any) => zoom(event, root));
      let view: any;
      zoomTo([focus.x, focus.y, focus.r * 2]);

      // Emit initial zoom state (at root)
      if (onZoomChange) {
        onZoomChange(true);
      }

      function zoomTo(v: any) {
        const k = width / v[2];
        view = v;
        label.attr(
          "transform",
          (d: any) => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`,
        );
        allNodes.topicCircles.attr(
          "transform",
          (d: any) => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`,
        );
        allNodes.topicCircles.attr("r", (d: any) => d.r * k);
        allNodes.testTriangles.attr(
          "transform",
          (d: any) => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`,
        );
        allNodes.testTriangles.attr("d", (d: any) => pentagonPath(d.r * k));
      }

      function zoom(event: any, d: any) {
        const focus0 = focus;
        focus = d;

        // Emit zoom state change
        if (onZoomChange) {
          onZoomChange(d === root);
        }

        const transition = svg
          .transition()
          .duration(event.altKey ? 7500 : 750)
          .tween("zoom", (d: any) => {
            const i = d3.interpolateZoom(view, [focus.x, focus.y, focus.r * 2]);
            return (t: any) => zoomTo(i(t));
          });

        // Only hide text if there's actually a transition (not clicking the same node)
        if (focus0 !== focus) {
          label.style("opacity", 0);
        }

        label
          .filter(function (d: any) {
            return shouldShowLabel(d, focus);
          })
          .transition(transition as any)
          .style("fill-opacity", (d: any) =>
            shouldShowLabel(d, focus) ? 1 : 0,
          )
          .on("start", function (d: any) {
            if (shouldShowLabel(d, focus))
              (this as any).style.display = "inline";
          })
          .on("end", function (d: any) {
            label.each(function (this: any, d: any) {
              // Always update display for all labels after zoom
              (this as any).style.display = shouldShowLabel(d, focus)
                ? "inline"
                : "none";
              if (!shouldShowLabel(d, focus)) return;
              const text = d.data.isTest
                ? d.data.originalInput || "Test"
                : d.data.name;
              const fontWeight = "bold";
              const k = width / focus.r / 2;
              const result = getMaxMultilineFontSize(
                text,
                d.r * k,
                fontWeight,
              ) as { fontSize: number; lines: string[] };
              d3.select(this).selectAll("tspan").remove();
              result.lines.forEach((line: string, i: number) => {
                d3.select(this)
                  .append("tspan")
                  .attr("x", 0)
                  .attr(
                    "y",
                    `${i * result.fontSize - ((result.lines.length - 1) * result.fontSize) / 2}`,
                  )
                  .style("font-size", `${result.fontSize}px`)
                  .attr("font-size", `${result.fontSize}px`)
                  .style("font-weight", "bold")
                  .attr("font-weight", "bold")
                  .style("fill", "#fff")
                  .attr("fill", "#fff")
                  .style("fill-opacity", 1)
                  .attr("fill-opacity", 1)
                  .style("stroke", "#000")
                  .attr("stroke", "#000")
                  .style("stroke-opacity", 1)
                  .attr("stroke-opacity", 1)
                  .style("stroke-width", "1.5px")
                  .attr("stroke-width", "1.5px")
                  .style("paint-order", "stroke fill")
                  .attr("paint-order", "stroke fill")
                  .style("font-family", "Arial, sans-serif")
                  .attr("font-family", "Arial, sans-serif")
                  .text(line);
              });
            });
            label.style("opacity", 1);
          });
      }

      // Append the chart to the container
      if (chartRef.current && svg.node()) {
        chartRef.current.appendChild(svg.node()!);
      }
    };

    // Initial render
    renderChart();

    // Add resize listener
    const handleResize = () => {
      renderChart();
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div ref={chartRef} className="max-h-[500px] flex  w-full rounded-xl" />
  );
};

// Helper function to wrap text for all nodes
function wrapText(text: string, r: number, fontSize: number) {
  // Estimate max chars per line based on node diameter
  const maxCharsPerLine = Math.floor((r * 1.8) / (fontSize * 0.6));
  if (text.length <= maxCharsPerLine) return [text];
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";
  for (const word of words) {
    if ((currentLine + " " + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + " " + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}
